import { FluentIterable } from "fluent-iterable";
import { ChangeSet, ChangeSetListener, filterChangeSetByProps } from "../DataStore/ChangeSet";
import { DataClient, DataSource, LocalDataStore } from "./LocalDataStore";
import { orderBy } from "lodash-es";

export type DataProviderArgs = {
    name: string;
    store: LocalDataStore;
};

export type DataProviderListener = {
    target: ChangeSetListener;
    paths: string[];
};

export abstract class DataProvider implements ChangeSetListener {
    public readonly name: string;
    protected readonly _store: LocalDataStore;
    protected readonly _dependsOn?: DataSource[];
    protected _listeners: DataProviderListener[] = [];

    constructor(args: DataProviderArgs, dependsOn?: DataSource[]) {
        console.assert(args?.store);

        this.name = args.name;
        this._store = args.store;
        this._dependsOn = dependsOn;

        if (this._dependsOn) {
            this._store.connectClient(new DataClient(this, this._dependsOn));
        }
    }

    public create(_props: any, ..._args: any[]): Promise<any> {
        throw new Error("Method not implemented.");
    }
    public read(_id: string, ..._args: any[]): Promise<any> {
        throw new Error("Method not implemented.");
    }
    public update(_props: any, ..._args: any[]): Promise<any> {
        throw new Error("Method not implemented.");
    }
    public delete(_id: string, ..._args: any[]): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public all(): Promise<FluentIterable<any>> {
        throw new Error("Method not implemented.");
    }

    public allOwnedBy(_owner: string): Promise<any> {
        throw new Error("Method not implemented.");
    }

    /**
     * Get the most recent record by a particular owner
     * @param owner Owner to get records for
     * @returns A single records or undefined if there are no records
     */
    public async newestOwnedBy(owner: string): Promise<any> {
        let all = Array.from(await this.allOwnedBy(owner));
        if (all) {
            if (all.length === 1) {
                return all[0];
            } else if (all.length > 1) {
                all = orderBy(all, ["createdAt"], ["asc"]);
                return all[0];
            }
        }
        return undefined;
    }

    public onChangeSet(_source: DataProvider, _changeSet: ChangeSet<any>): void {
        throw new Error("Method not implemented.");
    }

    public addListener(target: ChangeSetListener, paths: string[]) {
        const listener = { target, paths } as DataProviderListener;
        this._listeners.push(listener);
        this._raiseChangeOnAll(listener);
    }

    protected async _raiseChangeOnAll(listener: DataProviderListener) {
        // emit a change set with all existing items in it
        const items = await this.all();
        const changeSet = new ChangeSet([...items], [], []);
        this._notifyListener(listener, changeSet);
    }

    public removeListener(target: ChangeSetListener) {
        this._listeners = this._listeners.filter((x) => x.target !== target);
    }

    protected _notifyListeners(changeSet: ChangeSet<any>) {
        this._listeners.forEach((x) => {
            this._notifyListener(x, changeSet);
        });
    }

    protected _hasListeners() {
        return this._listeners.length > 0;
    }

    protected _notifyListener(listener: DataProviderListener, changeSet: ChangeSet<any>) {
        if (this._hasListeners() && changeSet.hasChanges()) {
            if (listener.paths.length > 0) {
                const targetChangeSet = filterChangeSetByProps(changeSet, listener.paths);
                if (targetChangeSet.hasChanges()) {
                    listener.target.onChangeSet(this, targetChangeSet);
                }
            } else {
                listener.target.onChangeSet(this, changeSet);
            }
        }
    }
}
